// MATERIAL UI
import {
  styled,
  TextField,
  Typography,
  Button,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";

export const Wrapper = styled("div")(({ theme }) => ({
  width: "100%",
  borderRadius: "30px",
  background: "#FFFFFF",
  boxShadow: "0px 4px 20px 0px rgba(173, 192, 203, 0.30)",
  padding: "24px 28px",
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  [theme.breakpoints.down("sm")]: {
    padding: "16px 20px",
    borderRadius: "20px",
  },
}));

export const HeaderWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  fontWeight: "bold",
  fontSize: "16px",
  color: "#fff",
}));

interface ClearIconWrapperProps {
  isLoading: boolean;
}
export const ClearIconWrapper = styled("div")<ClearIconWrapperProps>(({ isLoading }) => ({
  display: "flex",
  animation: isLoading ? "bezier-spin 1.5s cubic-bezier(0.42, 0, 0.58, 1) infinite" : "",
  "@keyframes bezier-spin": {
    from: { transform: "rotate(0deg)" },
    "to ": { transform: "rotate(360deg)" },
  },
}));

export const InputsWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  position: "relative",
}));

interface IInputWrapperProps {
  error?: boolean;
  padding?: string;
  border?: string;
  borderTop?: string;
  borderBottom?: string;
  borderLeft?: string;
  borderRight?: string;
  borderColor?: string;
  hoverStyles?: boolean | object;
  // Target Orders specific error props
  maxExceededError?: string;
  marketPriceError?: string;
  insufficientHoldings?: string;
  minSpendErr?: string;
}

export const InputWrapper = styled("div")<IInputWrapperProps>(
  ({
    theme,
    error,
    maxExceededError,
    marketPriceError,
    insufficientHoldings,
    minSpendErr,
    hoverStyles = true,
    padding = "16px",
    ...borderProps
  }) => {
    // Check if any error exists
    const hasError =
      error || maxExceededError || marketPriceError || insufficientHoldings || minSpendErr;

    return {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      flexDirection: "column",
      color: theme.palette.common.black,
      minHeight: "84px",
      width: "100%",
      padding,
      gap: "24px",
      border: borderProps.border ?? "1px solid #E5E7E8",
      borderTop: borderProps.borderTop,
      borderBottom: borderProps.borderBottom,
      borderLeft: borderProps.borderLeft,
      borderRight: borderProps.borderRight,
      borderColor: borderProps.borderColor,
      ...(hasError && {
        outline: "2px solid #FC4C4C",
        outlineOffset: "-2px",
      }),
      ...(!hasError &&
        hoverStyles !== false && {
          "&:hover":
            typeof hoverStyles === "object"
              ? hoverStyles
              : {
                  outline: "2px solid #002035",
                  outlineOffset: "-2px",
                },
        }),
    };
  },
);

export const CustomInputField = styled(TextField)(({ theme }) => ({
  width: "100%",
  "& .MuiInputBase-root": {
    padding: 0,
    width: "100%",
    gap: "24px",
  },
  "& .MuiInputBase-input": {
    fontSize: "28px",
    fontFamily: "Roobert",
    fontWeight: 500,
    [theme.breakpoints.down("sm")]: {
      fontSize: "16px",
    },
  },
}));

export const SummaryAndButtonWrapper = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "space-between",
  width: "100%",
  height: "100%",
}));

export const CalculationsWrapper = styled("div")(() => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  marginTop: "24px",
}));

interface CalcTextProps {
  weight?: number;
}
export const CalcText = styled("div")<CalcTextProps>(({ weight }) => ({
  color: "#002035",
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: weight ?? 500,
  lineHeight: "16px",
}));

export const CollapseContent = styled("div")(() => ({
  color: "#FFF",
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  padding: "12px",
  borderRadius: "10px",
}));

export const BreakdownContainer = styled("div")(() => ({
  background: "#F8F9F9",
  borderBottom: "1px solid #FFF",
  width: "100%",
  marginBottom: "14px",
  paddingBottom: "8px",
  borderRadius: "10px",
}));

export const TotalWrapper = styled("div")(() => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "8px",
  alignSelf: "stretch",
}));

export const ButtonText = styled("div")(() => ({
  fontSize: "18px",
  lineHeight: "22px",
  fontWeight: 600,
  fontFamily: "Roobert",
}));

// Component-specific styles that are commonly used but with variations
// These can be extended or overridden in component files if needed

export const FeeText = styled(Typography)(({ theme }) => ({
  display: "flex",
  alignSelf: "flex-end",
  color: "#5C6469",
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
  },
}));

export const PromotionalPlacard = styled("div")(() => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  color: "#056645",
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "16px",
}));

// Base icon wrapper that can be extended for specific icons
interface IconWrapperProps {
  clicked?: boolean;
  disable?: boolean;
}
export const IconWrapper = styled("div")<IconWrapperProps>(({ disable }) => ({
  position: "relative",
  margin: "-16px 0",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  color: "#FFFFFF",
  background: "#002035",
  borderRadius: "20px",
  width: "40px",
  height: "32px",
  cursor: disable ? "default" : "pointer",
  pointerEvents: disable ? "none" : "auto",
  "&:hover": {
    background: "#4E7086",
  },
}));

// Generic value text that can be used across components
interface IValueTextProps {
  color?: string;
  xsFontSize?: string;
  minSpendErr?: boolean;
}
export const ValueText = styled(Typography)<IValueTextProps>(
  ({ theme, color, xsFontSize, minSpendErr }) => ({
    color: minSpendErr ? "#FC4C4C" : color || "#002035",
    fontSize: "16px",
    fontFamily: "Roobert",
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
    },
    "@media (max-width: 400px)": {
      fontSize: xsFontSize || "14px",
    },
  }),
);

export const MaxButton = styled(Button)(({ theme }) => ({
  minWidth: "0",
  padding: "4px 8px",
  borderRadius: "20px",
  textTransform: "none",
  backgroundColor: "#002035",
  color: "#FFFFFF",
  maxHeight: "24px",
  lineHeight: "16px",
  fontSize: "14px",
  fontWeight: 600,
  "&:disabled": {
    backgroundColor: "#D1D4D6",
  },
  "&:hover": {
    backgroundColor: "#4E7086",
    color: "#FFFFFF",
  },
  "&:active": {
    backgroundColor: "#E8F6FF",
    color: "#002035",
  },
}));

export const StyledToggleButtonGroup = styled(ToggleButtonGroup)(() => ({
  display: "flex",
  padding: "4px",
  justifyContent: "center",
  borderRadius: "12px",
  border: "1px solid #E5E7E8",
  boxShadow: "0px 4px 20px 0px rgba(173, 192, 203, 0.30)",
  width: "173px",
  height: "44px",
}));

export const StyledToggleButton = styled(ToggleButton)(() => ({
  width: "100%",
  fontFamily: "Roobert",
  textTransform: "none",
  fontSize: "16px",
  fontWeight: 400,
  lineHeight: "20px",
  color: "#002035",
  border: "none",
  borderRadius: "12px !important",
  "&.Mui-selected": {
    backgroundColor: "#002035",
    color: "white",
    "&:hover": {
      backgroundColor: "#002035",
      color: "white",
    },
  },
}));
